import React, {useEffect, useState, useCallback} from "react";
import styles, {C} from "../styles";
import {formatDate, generateAvatar, getAnonymousAvatar} from "../utils";
import AvatarBadge from "../components/AvatarBadge";
import {apiCreatePost, apiDeletePost, apiLikePost} from "../api/posts";
import {apiCreateReport} from "../api/reports";
import {apiGetMoodsToday, apiSubmitMood} from "../api/moods";
import useIsMobile from "../hooks/useIsMobile";
import IdeaComments from "../components/IdeaComments";
import {useToast, useConfirm, usePrompt} from "../components/FeedbackProvider";

const MOOD_OPTIONS = [
    {level: 1, emoji: "😫", label: "Très difficile"},
    {level: 2, emoji: "😟", label: "Difficile"},
    {level: 3, emoji: "😐", label: "Correct"},
    {level: 4, emoji: "😊", label: "Bien"},
    {level: 5, emoji: "🤩", label: "Super"},
];

export default function IdeasPage({posts, user, selectedGroup, onRefresh}) {
    const {notify} = useToast();
    const confirm = useConfirm();
    const prompt = usePrompt();
    const AMORCE = selectedGroup ? `Et si chez ${selectedGroup.name} on ` : "";

    const [content, setContent] = useState(AMORCE);
    const [isAnonymous, setIsAnonymous] = useState(true);
    const [realName, setRealName] = useState("");
    const [publishing, setPublishing] = useState(false);
    const [formError, setFormError] = useState("");

    const [moodData, setMoodData] = useState(null);
    const [submittingMood, setSubmittingMood] = useState(false);

    const isAdmin = user?.role === "admin";
    const isMobile = useIsMobile();

    const loadMoods = useCallback(() => {
        if (selectedGroup && user) {
            apiGetMoodsToday(selectedGroup.id).then(setMoodData);
        }
    }, [selectedGroup, user]);

    // Réinitialise l'amorce quand le collectif change
    useEffect(() => {
        setContent(selectedGroup ? `Et si chez ${selectedGroup.name} on ` : "");
        setFormError("");
        loadMoods();
    }, [selectedGroup, loadMoods]);

    async function publishIdea() {
        if (!user || !selectedGroup) return;
        if (content.trim() === "" || content.trim() === AMORCE.trim()) {
            setFormError("Complétez l'amorce pour publier votre idée.");
            return;
        }
        if (!isAnonymous && realName.trim() === "") {
            setFormError("Entrez votre nom pour publier en votre nom.");
            return;
        }
        setFormError("");
        setPublishing(true);
        const data = await apiCreatePost(selectedGroup.id, content, isAnonymous, realName);
        setPublishing(false);
        if (data.error) { setFormError(data.error); return; }
        setContent(AMORCE);
        setRealName("");
        onRefresh();
    }

    async function deleteIdea(postId) {
        if (!(await confirm("Supprimer cette idée ?", {danger: true}))) return;
        apiDeletePost(postId).then(onRefresh);
    }

    function likeIdea(postId) {
        apiLikePost(postId).then(onRefresh);
    }

    async function reportIdea(postId) {
        if (!user) return;
        const reason = await prompt("Pourquoi souhaitez-vous signaler cette idée ?");
        if (reason === null) return;
        apiCreateReport("idea", postId, reason).then(() => notify("Signalement transmis au modérateur.", "success"));
    }

    function shareIdea(post) {
        const text = `💡 Proposition d'agent SAPPM\n\n"${post.content}"\n\nPartagé depuis Paroles d'Agents.`;
        navigator.clipboard.writeText(text);
        notify("Publication copiée dans le presse-papiers.", "success");
    }

    async function submitMood(level) {
        if (!selectedGroup || submittingMood) return;
        setSubmittingMood(true);
        const data = await apiSubmitMood(selectedGroup.id, level);
        setSubmittingMood(false);
        if (data.error) {
            notify(data.error, "error");
            return;
        }
        loadMoods();
    }

    const hasVoted = moodData?.userMood != null;
    const totalVotes = moodData?.total || 0;

    return (
        <>
            {/* Mon idée du jour — humeur */}
            {user && selectedGroup && (
                <div style={{
                    ...styles.card,
                    background: `linear-gradient(135deg, ${C.primaryLight} 0%, ${C.surface} 100%)`,
                    borderLeft: `4px solid ${C.primary}`,
                }}>
                    <h3 style={{margin: "0 0 4px", fontSize: 16, color: C.primary, fontFamily: "'Fraunces', serif"}}>
                        Mon humeur du jour
                    </h3>
                    <p style={{fontSize: 13, color: C.textMuted, margin: "0 0 14px"}}>
                        Comment ça va aujourd'hui ?
                    </p>

                    {!hasVoted ? (
                        <div style={{
                            display: "flex",
                            gap: isMobile ? 8 : 12,
                            justifyContent: "center",
                            flexWrap: "wrap",
                        }}>
                            {MOOD_OPTIONS.map(m => (
                                <button
                                    key={m.level}
                                    onClick={() => submitMood(m.level)}
                                    disabled={submittingMood}
                                    title={m.label}
                                    style={{
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        gap: 4,
                                        background: "white",
                                        border: `1.5px solid ${C.border}`,
                                        borderRadius: 12,
                                        padding: isMobile ? "10px 12px" : "12px 16px",
                                        cursor: submittingMood ? "wait" : "pointer",
                                        opacity: submittingMood ? 0.6 : 1,
                                        transition: "transform 0.15s, box-shadow 0.15s",
                                        fontSize: isMobile ? 28 : 32,
                                    }}
                                    onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.1)"; e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.12)"; }}
                                    onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.boxShadow = "none"; }}
                                >
                                    <span>{m.emoji}</span>
                                    <span style={{fontSize: 11, color: C.textMuted, fontWeight: 500}}>{m.label}</span>
                                </button>
                            ))}
                        </div>
                    ) : (
                        <div>
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                                marginBottom: 12,
                                padding: "8px 12px",
                                backgroundColor: "white",
                                borderRadius: 8,
                                border: `1px solid ${C.border}`,
                                width: "fit-content",
                            }}>
                                <span style={{fontSize: 22}}>
                                    {MOOD_OPTIONS.find(m => m.level === moodData.userMood)?.emoji}
                                </span>
                                <span style={{fontSize: 13, color: C.text, fontWeight: 500}}>
                                    Vous : {MOOD_OPTIONS.find(m => m.level === moodData.userMood)?.label}
                                </span>
                            </div>

                            <p style={{fontSize: 12, color: C.textMuted, marginBottom: 8}}>
                                {totalVotes} agent{totalVotes > 1 ? "s" : ""} {totalVotes > 1 ? "ont" : "a"} partagé {totalVotes > 1 ? "leur" : "son"} humeur aujourd'hui
                            </p>

                            <div style={{display: "flex", flexDirection: "column", gap: 6}}>
                                {MOOD_OPTIONS.map(m => {
                                    const count = moodData.results[m.level] || 0;
                                    const pct = totalVotes > 0 ? Math.round((count / totalVotes) * 100) : 0;
                                    return (
                                        <div key={m.level} style={{display: "flex", alignItems: "center", gap: 8}}>
                                            <span style={{fontSize: 18, width: 28, textAlign: "center"}}>{m.emoji}</span>
                                            <div style={{
                                                flex: 1,
                                                height: 22,
                                                backgroundColor: "#F3F4F6",
                                                borderRadius: 6,
                                                overflow: "hidden",
                                                position: "relative",
                                            }}>
                                                <div style={{
                                                    height: "100%",
                                                    width: `${pct}%`,
                                                    backgroundColor: C.primary,
                                                    borderRadius: 6,
                                                    transition: "width 0.4s ease",
                                                    opacity: 0.7,
                                                }} />
                                                {pct > 0 && (
                                                    <span style={{
                                                        position: "absolute",
                                                        top: "50%",
                                                        left: 8,
                                                        transform: "translateY(-50%)",
                                                        fontSize: 11,
                                                        fontWeight: 600,
                                                        color: pct > 15 ? "white" : C.text,
                                                    }}>
                                                        {pct}%
                                                    </span>
                                                )}
                                            </div>
                                            <span style={{fontSize: 12, color: C.textMuted, minWidth: 20, textAlign: "right"}}>
                                                {count}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Formulaire de publication */}
            {user && (
                <div style={styles.card}>
                    <h3 style={{margin: "0 0 12px", fontSize: 16, fontFamily: "'Fraunces', serif"}}>💡 Proposer une idée</h3>

                    {/* Toggle anonymat */}
                    <div style={{
                        display: "flex",
                        gap: 0,
                        marginBottom: 12,
                        border: `1.5px solid ${C.border}`,
                        borderRadius: 8,
                        overflow: "hidden",
                        width: "fit-content",
                    }}>
                        <button
                            onClick={() => { setIsAnonymous(true); setFormError(""); }}
                            style={{
                                padding: "8px 16px",
                                border: "none",
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: 13,
                                backgroundColor: isAnonymous ? C.primary : "white",
                                color: isAnonymous ? "white" : C.textMuted,
                                transition: "background-color 0.15s",
                            }}
                        >
                            🔒 Anonyme
                        </button>
                        <button
                            onClick={() => { setIsAnonymous(false); setFormError(""); }}
                            style={{
                                padding: "8px 16px",
                                border: "none",
                                borderLeft: `1.5px solid ${C.border}`,
                                cursor: "pointer",
                                fontWeight: 600,
                                fontSize: 13,
                                backgroundColor: !isAnonymous ? C.primary : "white",
                                color: !isAnonymous ? "white" : C.textMuted,
                                transition: "background-color 0.15s",
                            }}
                        >
                            ✍️ En mon nom
                        </button>
                    </div>

                    {/* Explication du mode choisi */}
                    <p style={{fontSize: 12, color: C.textMuted, marginBottom: 10, marginTop: -4}}>
                        {isAnonymous
                            ? <>Votre pseudonyme anonyme : <strong style={{color: C.text}}><AvatarBadge avatar={getAnonymousAvatar({author: user.username})} size={18}/> {user.username}</strong></>
                            : "Votre idée sera publiée avec le nom que vous entrez ci-dessous."}
                    </p>

                    {/* Champ nom réel (mode "En mon nom" seulement) */}
                    {!isAnonymous && (
                        <input
                            value={realName}
                            onChange={e => setRealName(e.target.value)}
                            placeholder="Votre prénom et nom (ex : Marie Dupont)"
                            style={{...styles.input, marginBottom: 10}}
                        />
                    )}

                    {/* Textarea avec amorce pré-remplie */}
                    <textarea
                        value={content}
                        onChange={e => setContent(e.target.value)}
                        placeholder={AMORCE || "Partagez votre idée..."}
                        style={{...styles.input, height: 100, resize: "vertical"}}
                    />

                    {formError && (
                        <p style={{color: C.danger, fontSize: 13, marginBottom: 8, marginTop: -4}}>{formError}</p>
                    )}

                    <button onClick={publishIdea} disabled={publishing} style={{...styles.button, opacity: publishing ? 0.7 : 1}}>
                        {publishing ? "Publication…" : "Publier mon idée"}
                    </button>
                </div>
            )}

            {posts.length === 0 && (
                <p style={{color: C.textMuted, fontSize: 14, textAlign: "center", marginTop: 24}}>
                    Aucune idée publiée pour l'instant dans ce collectif — la vôtre pourrait être la première.
                </p>
            )}

            {/* Liste des idées */}
            {posts.map(post => {
                const anonymous = post.is_anonymous === 1;
                const postAvatar = anonymous ? getAnonymousAvatar(post) : generateAvatar(post.author);

                return (
                    <div key={post.id} style={{...styles.card, borderLeft: `4px solid ${C.accent}`}}>
                        <div style={{display: "flex", alignItems: "center", gap: 12, marginBottom: 8}}>
                            <div style={{
                                ...styles.avatarCircle,
                                width: 46,
                                height: 46,
                                fontSize: 24,
                                backgroundColor: postAvatar.color,
                            }}>
                                <AvatarBadge avatar={postAvatar}/>
                            </div>
                            <div>
                                <div style={{display: "flex", alignItems: "center", gap: 8}}>
                                    <strong>{post.author}</strong>
                                    {anonymous && (
                                        <span style={{
                                            fontSize: 11,
                                            fontWeight: 600,
                                            backgroundColor: "#F3F4F6",
                                            color: "#6B7280",
                                            border: "1px solid #D1D5DB",
                                            borderRadius: 20,
                                            padding: "1px 8px",
                                            letterSpacing: 0.3,
                                        }}>
                                            Anonyme
                                        </span>
                                    )}
                                </div>
                                <small style={{color: "#6c8b74"}}>
                                    Agent · {formatDate(post.created_at)}
                                </small>
                            </div>
                        </div>

                        <p style={{
                            fontSize: isMobile ? 14 : 16,
                            lineHeight: isMobile ? "20px" : "24px",
                            marginTop: 0,
                            marginBottom: 10,
                        }}>
                            {post.content}
                        </p>

                        <div style={{display: "flex", flexWrap: "wrap", gap: 4}}>
                            <button onClick={() => likeIdea(post.id)} style={styles.likeButton}>
                                ❤️ Soutenir ({post.likes})
                            </button>
                            <button onClick={() => shareIdea(post)} style={styles.button}>
                                ↗️ Partager
                            </button>
                            <button onClick={() => reportIdea(post.id)} style={styles.button}>
                                🚩 Signaler
                            </button>
                            {isAdmin && (
                                <button onClick={() => deleteIdea(post.id)} style={styles.dangerButton}>
                                    Supprimer
                                </button>
                            )}
                        </div>
                        <IdeaComments postId={post.id} initialCount={post.comment_count || 0} user={user}/>
                    </div>
                );
            })}
        </>
    );
}
